import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import {
  Headphones,
  FileText,
  BarChart3,
  Video,
  Microscope,
  Code2,
  ArrowRight,
  LucideIcon,
} from 'lucide-react';
import { FlowTemplate } from '../../types/flows';

const iconMap: Record<string, LucideIcon> = {
  Headphones,
  FileText,
  BarChart3,
  Video,
  Microscope,
  Code2,
};

const colorClasses: Record<string, { bg: string; border: string; dot: string; text: string }> = {
  emerald: { bg: 'bg-transparent', border: 'border-emerald-500/30 dark:border-emerald-500/30', dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
  orange:  { bg: 'bg-transparent', border: 'border-orange-500/30 dark:border-orange-500/30',   dot: 'bg-orange-500',  text: 'text-orange-700 dark:text-orange-400' },
  blue:    { bg: 'bg-transparent', border: 'border-blue-500/30 dark:border-blue-500/30',       dot: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-400' },
  purple:  { bg: 'bg-transparent', border: 'border-purple-500/30 dark:border-purple-500/30',   dot: 'bg-purple-500',  text: 'text-purple-700 dark:text-purple-400' },
  amber:   { bg: 'bg-transparent', border: 'border-amber-500/30 dark:border-amber-500/30',     dot: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-400' },
  violet:  { bg: 'bg-transparent', border: 'border-violet-500/30 dark:border-violet-500/30',   dot: 'bg-violet-500',  text: 'text-violet-700 dark:text-violet-400' },
};

export const FlowCard: React.FC<{ template: FlowTemplate }> = ({ template }) => {
  const navigate = useNavigate();
  const Icon = iconMap[template.icon] || BarChart3;
  const colors = colorClasses[template.color] || colorClasses.blue;

  return (
    <button
      onClick={() => navigate(`/flows/${template.id}`)}
      className={`text-left w-full rounded-xl border-2 p-5 transition-all hover:shadow-lg hover:scale-[1.02] ${colors.bg} ${colors.border}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center"
          style={{ background: template.colorHex }}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <ArrowRight className={`h-4 w-4 mt-1 ${colors.text}`} />
      </div>

      <h3 className="font-semibold text-sm mb-1">{template.name}</h3>
      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
        {template.description}
      </p>

      {/* Step dots */}
      <div className="flex items-center gap-1 mb-3">
        {template.steps.map((step, i) => (
          <React.Fragment key={step.id}>
            <div className={`h-2 w-2 rounded-full ${colors.dot}`} title={step.label} />
            {i < template.steps.length - 1 && (
              <div className={`h-px w-3 ${colors.dot} opacity-40`} />
            )}
          </React.Fragment>
        ))}
        <span className="text-[10px] text-muted-foreground ml-1.5">
          {template.steps.length} steps
        </span>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1">
        {template.tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
            {tag}
          </Badge>
        ))}
      </div>
    </button>
  );
};
